// A5 — Gestión de usuarios + asignación de plantas/líneas.
// El Admin crea coordinador/supervisor/líder y les asigna plantas/líneas
// (tenant-wide). Esa asignación define el scope que el Coordinador luego ve.

import { useState } from 'react';
import { toast } from 'sonner';
import type { AdminUsuario, AdminUsuariosList } from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { TBody, TD, TH, THead, TR, Table } from '../../components/ui/table';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { AdminSidebar } from '../../components/layout/AdminSidebar';
import { identidadUsuario } from '../../lib/identidad';
import {
  useAdminUsuarios,
  useCreateAdminUsuario,
  useUpdateAdminUsuario,
  useAdminReasignarPlantas,
  useAdminReasignarLineas,
} from '../../lib/api/queries';

const RAZON_MIN = 20;

export function A5UsuariosPage() {
  const { data, isLoading, isError } = useAdminUsuarios();
  const [crear, setCrear] = useState(false);
  const [asignar, setAsignar] = useState<AdminUsuario | null>(null);

  return (
    <RoleLayout sidebar={<AdminSidebar />} roleLabel="Admin del Tenant">
      <div className="mx-auto flex max-w-5xl flex-col gap-4">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-medium text-fg">Gestión de usuarios</h1>
            <p className="mt-1 text-[11px] text-fg-tertiary">
              Coordinadores · supervisores · líderes del tenant
            </p>
          </div>
          <Button onClick={() => setCrear(true)}>Nuevo usuario</Button>
        </header>
        {isLoading || !data ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            {isError ? <span className="text-sm text-crit">No se pudo cargar.</span> : <Spinner />}
          </div>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Usuario</TH>
                <TH>Rol</TH>
                <TH>Asignación</TH>
                <TH align="center">Estado</TH>
                <TH align="right" />
              </TR>
            </THead>
            <TBody>
              {data.usuarios.map((u) => (
                <TR key={u.user_id}>
                  <TD>
                    <span className="font-medium text-fg">
                      {u.nombre} {u.apellido}
                    </span>
                    <div className="text-[11px] text-fg-tertiary">
                      {identidadUsuario(u.email).etiqueta}
                    </div>
                  </TD>
                  <TD>
                    <Badge tone="neutral">{u.rol}</Badge>
                  </TD>
                  <TD className="text-xs text-fg-secondary">
                    {u.rol === 'lider'
                      ? u.lineas.map((l) => l.codigo).join(', ') || '—'
                      : u.plantas.map((p) => p.nombre).join(', ') || '—'}
                  </TD>
                  <TD align="center">
                    <Badge tone={u.activo ? 'ok' : 'neutral'}>
                      {u.activo ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </TD>
                  <TD align="right">
                    <Button size="sm" variant="ghost" onClick={() => setAsignar(u)}>
                      Gestionar
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </div>
      {crear && data && <CrearDialog recursos={data.recursos} onClose={() => setCrear(false)} />}
      {asignar && data && (
        <GestionarDialog
          usuario={asignar}
          recursos={data.recursos}
          onClose={() => setAsignar(null)}
        />
      )}
    </RoleLayout>
  );
}

function CrearDialog({
  recursos,
  onClose,
}: {
  recursos: AdminUsuariosList['recursos'];
  onClose: () => void;
}) {
  const crear = useCreateAdminUsuario();
  const [f, setF] = useState({
    email: '',
    nombre: '',
    apellido: '',
    rol: 'coordinador' as 'coordinador' | 'supervisor' | 'lider',
  });
  const [plantaIds, setPlantaIds] = useState<string[]>([]);
  const [lineaIds, setLineaIds] = useState<string[]>([]);
  const esLider = f.rol === 'lider';

  return (
    <Dialog open onClose={onClose} className="max-w-lg">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nuevo usuario</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <Campo label="Email" value={f.email} onChange={(v) => setF({ ...f, email: v })} />
          <div className="grid grid-cols-2 gap-3">
            <Campo label="Nombre" value={f.nombre} onChange={(v) => setF({ ...f, nombre: v })} />
            <Campo
              label="Apellido"
              value={f.apellido}
              onChange={(v) => setF({ ...f, apellido: v })}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Rol</Label>
            <Select
              value={f.rol}
              onChange={(e) => setF({ ...f, rol: e.target.value as typeof f.rol })}
            >
              <option value="coordinador">Coordinador</option>
              <option value="supervisor">Supervisor</option>
              <option value="lider">Líder</option>
            </Select>
          </div>
          {esLider ? (
            <MultiCheck
              label="Líneas"
              items={recursos.lineas.map((l) => ({ id: l.id, label: l.codigo }))}
              selected={lineaIds}
              onToggle={setLineaIds}
            />
          ) : (
            <MultiCheck
              label="Plantas"
              items={recursos.plantas.map((p) => ({ id: p.id, label: p.nombre }))}
              selected={plantaIds}
              onToggle={setPlantaIds}
            />
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={
              crear.isPending ||
              !f.email.trim() ||
              !f.nombre.trim() ||
              !f.apellido.trim() ||
              (esLider ? lineaIds.length === 0 : plantaIds.length === 0)
            }
            onClick={() =>
              crear.mutate(
                {
                  email: f.email.trim(),
                  nombre: f.nombre.trim(),
                  apellido: f.apellido.trim(),
                  rol: f.rol,
                  planta_ids: esLider ? [] : plantaIds,
                  linea_ids: esLider ? lineaIds : [],
                },
                {
                  onSuccess: (res) => {
                    // Respuesta del backend (A5): { invitado: boolean, email: string }.
                    // okSchema es passthrough → leemos los campos defensivamente.
                    const r = res as { invitado?: boolean; email?: string };
                    const correo = r.email ?? f.email.trim();
                    if (r.invitado) {
                      toast.success(
                        `Invitación enviada a ${correo} — el usuario define su contraseña desde el correo`,
                      );
                    } else {
                      toast.success(`${correo} ya tenía cuenta; se agregó al tenant`);
                    }
                    onClose();
                  },
                  onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Error'),
                },
              )
            }
          >
            Crear
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GestionarDialog({
  usuario,
  recursos,
  onClose,
}: {
  usuario: AdminUsuario;
  recursos: AdminUsuariosList['recursos'];
  onClose: () => void;
}) {
  const update = useUpdateAdminUsuario();
  const reasignarP = useAdminReasignarPlantas();
  const reasignarL = useAdminReasignarLineas();
  const esLider = usuario.rol === 'lider';
  const [plantaIds, setPlantaIds] = useState<string[]>(usuario.plantas.map((p) => p.id));
  const [lineaIds, setLineaIds] = useState<string[]>(usuario.lineas.map((l) => l.id));
  const [razon, setRazon] = useState('');
  const [activo, setActivo] = useState(usuario.activo);
  const desactivando = usuario.activo && !activo;

  function guardarAsignacion() {
    const opts = {
      onSuccess: () => {
        toast.success('Asignación actualizada');
        onClose();
      },
      onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Error'),
    };
    if (esLider)
      reasignarL.mutate(
        {
          id: usuario.user_id,
          body: {
            linea_ids: lineaIds,
            razon: razon.trim() || 'Reasignación de líneas desde gestión de usuarios',
          },
        },
        opts,
      );
    else
      reasignarP.mutate(
        {
          id: usuario.user_id,
          body: {
            planta_ids: plantaIds,
            razon: razon.trim() || 'Reasignación de plantas desde gestión de usuarios',
          },
        },
        opts,
      );
  }
  function guardarEstado() {
    update.mutate(
      { id: usuario.user_id, body: { activo, ...(desactivando ? { razon: razon.trim() } : {}) } },
      {
        onSuccess: () => {
          toast.success('Estado actualizado');
          onClose();
        },
        onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Error'),
      },
    );
  }

  return (
    <Dialog open onClose={onClose} className="max-w-lg">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {usuario.nombre} {usuario.apellido} · {usuario.rol}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div>
            <span className="text-[10px] font-medium uppercase tracking-wide text-fg-tertiary">
              Asignación de {esLider ? 'líneas' : 'plantas'}
            </span>
            {esLider ? (
              <MultiCheck
                label=""
                items={recursos.lineas.map((l) => ({ id: l.id, label: l.codigo }))}
                selected={lineaIds}
                onToggle={setLineaIds}
              />
            ) : (
              <MultiCheck
                label=""
                items={recursos.plantas.map((p) => ({ id: p.id, label: p.nombre }))}
                selected={plantaIds}
                onToggle={setPlantaIds}
              />
            )}
            <Button
              size="sm"
              className="mt-2"
              disabled={
                reasignarP.isPending ||
                reasignarL.isPending ||
                (esLider ? lineaIds.length === 0 : plantaIds.length === 0)
              }
              onClick={guardarAsignacion}
            >
              Guardar asignación
            </Button>
          </div>
          <div className="border-t border-border pt-3">
            <label className="flex items-center gap-2 text-sm text-fg">
              <input
                type="checkbox"
                checked={activo}
                onChange={(e) => setActivo(e.target.checked)}
              />
              Activo
            </label>
            {desactivando && (
              <div className="mt-2 flex flex-col gap-1">
                <Label>Razón de desactivación (≥{RAZON_MIN})</Label>
                <Input value={razon} onChange={(e) => setRazon(e.target.value)} />
              </div>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="mt-2"
              disabled={
                update.isPending ||
                activo === usuario.activo ||
                (desactivando && razon.trim().length < RAZON_MIN)
              }
              onClick={guardarEstado}
            >
              Guardar estado
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MultiCheck({
  label,
  items,
  selected,
  onToggle,
}: {
  label: string;
  items: Array<{ id: string; label: string }>;
  selected: string[];
  onToggle: (ids: string[]) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      {label && <Label>{label}</Label>}
      <div className="flex max-h-40 flex-wrap gap-2 overflow-y-auto rounded border border-border p-2">
        {items.map((it) => {
          const on = selected.includes(it.id);
          return (
            <button
              key={it.id}
              type="button"
              onClick={() =>
                onToggle(on ? selected.filter((x) => x !== it.id) : [...selected, it.id])
              }
              className={`rounded border px-2 py-0.5 text-xs ${on ? 'border-accent bg-accent-soft text-accent' : 'border-border text-fg-secondary'}`}
            >
              {it.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Campo({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label>{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
